import FileService from "@/api/shared/file";
import { useSnackStore } from "@/stores/snackStore";
import { useAuthStore } from "@/stores/authStore";
import { djangoErrorHandler } from "@/utils/DjangoError";
import { useMutation, useQueries } from "@tanstack/vue-query";
import type { TFile } from "@/api/shared/file";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter.ts";
import { useGenericQuery } from "@/composables/shared/queries/useGenericQuery.ts";
import { createQueryKeys } from "@/composables/shared/queries/useQueryKeyBuilder";
import type { TItemQueryBehaviorOptions } from "@/composables/shared/queries";
import { computed, type MaybeRefOrGetter, ref, toValue } from "vue";
import { storeToRefs } from "pinia";
import { usePreviews } from "@/composables/shared/usePreviews.ts";

const MB = 1024 * 1024;
export const MAX_FILE_SIZE_BYTES = Number(import.meta.env.VITE_MAX_FILE_SIZE_MB ?? 50) * MB;

export interface TFileUploadProgress {
  key: string;
  progress: number;
  status: EFileUploadStatus;
  error?: string;
  fileName?: string;
}

export enum EFileUploadStatus {
  UPLOADING = "uploading",
  COMPLETED = "completed",
  ERROR = "error",
}

export enum EFileType {
  AUDIO = "audio",
  VIDEO = "video",
  IMAGE = "image",
  DOCUMENT = "document",
  TEXT = "text",
  APPLICATION = "application",
  MODEL = "model",
  UNKNOWN = "unknown",
}

const EXT_TO_TYPE: Readonly<Record<string, EFileType>> = {
  jpg: EFileType.IMAGE,
  jpeg: EFileType.IMAGE,
  png: EFileType.IMAGE,
  gif: EFileType.IMAGE,
  webp: EFileType.IMAGE,
  bmp: EFileType.IMAGE,
  svg: EFileType.IMAGE,
  heic: EFileType.IMAGE,
  heif: EFileType.IMAGE,

  mp3: EFileType.AUDIO,
  wav: EFileType.AUDIO,
  ogg: EFileType.AUDIO,
  flac: EFileType.AUDIO,
  m4a: EFileType.AUDIO,
  aac: EFileType.AUDIO,

  mp4: EFileType.VIDEO,
  mov: EFileType.VIDEO,
  mkv: EFileType.VIDEO,
  webm: EFileType.VIDEO,
  avi: EFileType.VIDEO,

  pdf: EFileType.DOCUMENT,
  doc: EFileType.DOCUMENT,
  docx: EFileType.DOCUMENT,
  xls: EFileType.DOCUMENT,
  xlsx: EFileType.DOCUMENT,
  ppt: EFileType.DOCUMENT,
  pptx: EFileType.DOCUMENT,

  txt: EFileType.TEXT,
  csv: EFileType.TEXT,
  json: EFileType.TEXT,
  xml: EFileType.TEXT,
  md: EFileType.TEXT,
  yaml: EFileType.TEXT,
  yml: EFileType.TEXT,

  stl: EFileType.MODEL,
  ply: EFileType.MODEL,

  zip: EFileType.APPLICATION,
  rar: EFileType.APPLICATION,
  "7z": EFileType.APPLICATION,
  exe: EFileType.APPLICATION,
  dmg: EFileType.APPLICATION,
  iso: EFileType.APPLICATION,
};

const MIME_PREFIX_TO_TYPE: ReadonlyArray<[string, EFileType]> = [
  ["image/", EFileType.IMAGE],
  ["audio/", EFileType.AUDIO],
  ["video/", EFileType.VIDEO],
  ["text/", EFileType.TEXT],
  ["application/", EFileType.APPLICATION],
];

const TYPE_TO_MIME_WILDCARD: Partial<Record<EFileType, string>> = {
  [EFileType.IMAGE]: "image/*",
  [EFileType.AUDIO]: "audio/*",
  [EFileType.VIDEO]: "video/*",
  [EFileType.TEXT]: "text/*",
  [EFileType.APPLICATION]: "application/*",
};

export const getExtensionsForFileType = (type: EFileType): string[] =>
  Object.entries(EXT_TO_TYPE).flatMap(([extension, fileType]) =>
    fileType === type ? [extension] : []
  );

export function buildFileAccept(options: {
  extensions?: string[];
  types?: EFileType | EFileType[];
}): string | null {
  if (options.extensions?.length) {
    return options.extensions.map((extension) => `.${extension}`).join(",");
  }

  const requested = options.types ?? [];
  const types = Array.isArray(requested) ? requested : [requested];

  const tokens = types.flatMap(
    (type) =>
      TYPE_TO_MIME_WILDCARD[type] ??
      getExtensionsForFileType(type).map((extension) => `.${extension}`)
  );

  return tokens.length > 0 ? [...new Set(tokens)].join(",") : null;
}

export const isFileTooLarge = (file: File, maxBytes = MAX_FILE_SIZE_BYTES) => file.size > maxBytes;

export function triggerDownload(blob: Blob, suggestedName = "download") {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function downloadSequentially<T>(
  items: readonly T[],
  download: (item: T) => Promise<void>
): Promise<void> {
  for (const item of items) {
    await download(item);
  }
}

export type TDownloadableFile = {
  file: TFile;
  blob?: Blob;
};

export const MULTI_DOWNLOAD_GAP_MS = 300;

function waitForMacrotask(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useFile() {
  const { getRawFile } = usePreviews();

  const uploadProgress = ref<Map<string, TFileUploadProgress>>(new Map());
  const { accessToken } = storeToRefs(useAuthStore());

  function updateProgress(
    key: string,
    progress: number,
    status: EFileUploadStatus,
    options?: {
      error?: string;
      fileName?: string;
    }
  ) {
    uploadProgress.value.set(key, {
      key,
      progress,
      status,
      error: options?.error,
      fileName: options?.fileName,
    });
  }

  function removeProgress(key: string) {
    uploadProgress.value.delete(key);
  }

  function clearAllProgress() {
    uploadProgress.value.clear();
  }

  const uploadMutation = useMutation({
    mutationFn: async (args: { formData: FormData; progressKey: string; fileName?: string }) => {
      const { formData, progressKey, fileName } = args;

      return await FileService.upload(formData, (e) => {
        if (!e.total) return;

        const percent = Math.min(100, Math.max(0, (e.loaded / e.total) * 100));
        updateProgress(progressKey, percent, EFileUploadStatus.UPLOADING, { fileName });
      });
    },
    onError: (error) => {
      logError(error, {
        context: {
          feature: "file",
          step: "uploadFile",
        },
      });
      djangoErrorHandler(useSnackStore())(error);
    },
  });

  const uploadFile = async (formData: FormData, progressKey: string, fileName?: string) => {
    return await uploadMutation.mutateAsync({
      formData,
      progressKey,
      fileName,
    });
  };

  const uploadFileWithProgress = async (
    formData: FormData,
    progressKey: string,
    fileName?: string
  ) => {
    updateProgress(progressKey, 0, EFileUploadStatus.UPLOADING, { fileName });

    try {
      const response = await uploadFile(formData, progressKey, fileName);

      updateProgress(progressKey, 100, EFileUploadStatus.COMPLETED, { fileName });
      setTimeout(() => removeProgress(progressKey), 800);

      return response;
    } catch (error) {
      updateProgress(progressKey, 0, EFileUploadStatus.ERROR, {
        error: String(error),
        fileName,
      });

      logError(error, {
        context: {
          feature: "file",
          step: "uploadFileWithProgress",
          progressKey,
          fileName,
        },
      });

      throw error;
    }
  };

  const openFileInNewTab = async (file?: TFile, url?: string) => {
    const targetUrl = file?.file ?? url ?? null;
    if (!targetUrl) return;

    // Without raw=true the media service returns a generated file-type icon
    // thumbnail for non-image files (e.g. a red XLSX glyph) instead of the file.
    window.open(getRawFile(targetUrl), "_blank", "noopener,noreferrer");
  };

  type TDownloadProgress = {
    loaded: number;
    total: number | null;
    percent: number | null;
  };

  const getFileBlob = async (
    file: TFile,
    onProgress?: (progress: TDownloadProgress) => void
  ): Promise<Blob> => {
    const url = getRawFile(file.file);

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: accessToken.value
        ? {
            Authorization: `Bearer ${accessToken.value}`,
          }
        : undefined,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }

    if (!response.body) {
      const blob = await response.blob();
      onProgress?.({
        loaded: blob.size,
        total: blob.size,
        percent: 100,
      });
      return blob;
    }

    const totalHeader = response.headers.get("content-length");
    const total = totalHeader ? Number(totalHeader) : null;

    const reader = response.body.getReader();
    const chunks: ArrayBuffer[] = [];
    let loaded = 0;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;
      if (!value) continue;

      loaded += value.byteLength;

      const chunk = new Uint8Array(value.byteLength);
      chunk.set(value);
      chunks.push(chunk.buffer);

      onProgress?.({
        loaded,
        total,
        percent: total ? Math.round((loaded / total) * 100) : null,
      });
    }

    return new Blob(chunks, {
      type: response.headers.get("content-type") || file.mimetype || "application/octet-stream",
    });
  };

  const toNativeFile = async (
    file: TFile,
    onProgress?: (progress: TDownloadProgress) => void
  ): Promise<File> => {
    const blob = await getFileBlob(file, onProgress);

    return new File([blob], file.original_name || file.name, {
      type: blob.type || file.mimetype,
      lastModified: new Date(file.uploaded_at ?? file.created_at).getTime(),
    });
  };

  const toNativeFiles = async (files: TFile[]): Promise<File[]> => {
    return await Promise.all(files.map((file) => toNativeFile(file)));
  };

  const downloadFile = async (file: TFile, suggestedName = "download") => {
    const blob = await getFileBlob(file);
    triggerDownload(blob, suggestedName);
  };

  const downloadFiles = async (files: readonly TDownloadableFile[]) => {
    let isFirst = true;
    await downloadSequentially(files, async ({ file, blob }) => {
      if (!isFirst) await waitForMacrotask(MULTI_DOWNLOAD_GAP_MS);
      isFirst = false;
      triggerDownload(blob ?? (await getFileBlob(file)), file.original_name || file.name);
    });
  };

  const downloadFromUrl = async (url: string, suggestedName = "download") => {
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: accessToken.value
        ? {
            Authorization: `Bearer ${accessToken.value}`,
          }
        : undefined,
    });

    const blob = await response.blob();
    triggerDownload(blob, suggestedName);
  };

  return {
    uploadFile,
    uploadFileWithProgress,
    getFileBlob,
    toNativeFile,
    toNativeFiles,
    downloadFile,
    downloadFiles,
    downloadFromUrl,
    openFileInNewTab,
    uploadProgress,
    updateProgress,
    removeProgress,
    clearAllProgress,
  };
}

export function getExtension(nameOrUrl: string): string | null {
  const clean = nameOrUrl.trim().toLowerCase();
  const base = clean.split("#")[0]?.split("?")[0] ?? clean;

  const lastDot = base.lastIndexOf(".");
  if (lastDot === -1) return null;

  const ext = base.slice(lastDot + 1);
  return ext || null;
}

export function getFileTypeFromExtension(nameOrUrl?: string): EFileType {
  if (!nameOrUrl) {
    return EFileType.UNKNOWN;
  }

  const ext = getExtension(nameOrUrl);
  if (!ext) {
    return EFileType.UNKNOWN;
  }

  return EXT_TO_TYPE[ext] ?? EFileType.UNKNOWN;
}

export function getFileTypeFromMime(mime?: string | null): EFileType {
  const m = (mime ?? "").trim().toLowerCase();
  if (!m) return EFileType.UNKNOWN;

  for (const [prefix, type] of MIME_PREFIX_TO_TYPE) {
    if (m.startsWith(prefix)) return type;
  }

  return EFileType.UNKNOWN;
}

export function getFileType(file?: File | TFile): EFileType {
  if (!file) return EFileType.UNKNOWN;

  if (file instanceof File) {
    const byMime = getFileTypeFromMime(file.type);
    return byMime !== EFileType.UNKNOWN ? byMime : getFileTypeFromExtension(file.name);
  }

  const byMime = getFileTypeFromMime(file.mimetype);
  const name = file.original_name || file.name || "";

  if (byMime === EFileType.UNKNOWN || byMime === EFileType.APPLICATION) {
    return getFileTypeFromExtension(name);
  }

  return byMime;
}

export const FileQueryKeys = createQueryKeys(FileService.queryKey, (key) => ({
  detail: key<string>("detail"),
}));

export const useFileDetail = (
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery<TFile, string>({
    queryKey: FileQueryKeys.detail(),
    params,
    fetcher: (id) => FileService.detail({ id }),
    ...options,
  });
};

export const useFileDetails = (fileIds: MaybeRefOrGetter<string[] | undefined>) => {
  const resolvedFileIds = computed(() => (toValue(fileIds) ?? []).filter(Boolean));

  return useQueries({
    queries: computed(() =>
      resolvedFileIds.value.map((id) => ({
        queryKey: ["file-detail", id],
        queryFn: async (): Promise<TFile> => {
          const response = await FileService.detail({ id });
          return response.data;
        },
        enabled: !!id,
      }))
    ),
  });
};

export const useFilesUploading = <T>(values: T, fields: (keyof T)[]) =>
  computed(() => {
    const checkFiles = (files: unknown) => {
      if (Array.isArray(files)) {
        return files.some((f) => (f as TFile | undefined)?.isUploading);
      }

      return (files as TFile | undefined)?.isUploading;
    };

    return fields.some((field) => checkFiles(values[field]));
  });
